import React from 'react';
import Axios from 'axios';
import twitchLogo from '../images/platform/twitch.png';
import steamLogo from '../images/platform/steam.png';
import './DropsPage.scss';
import './DropsLinkStartPage.scss';

const DROPS_STATE_KEY = 'noizDropsOauthState';

class DropsLinkStartPage extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      status: 'loading',
      error: null,
      cfg: null,
      linkStatus: null,
      computedRedirectUri: null,
      oauthUrl: null,
      debug: false,
    };
  }

  componentDidMount() {
    this.load();
  }

  getToken = () => {
    return this.props?.auth?.token || null;
  };

  clearLocalAuth = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (e) {
      // ignore
    }
  };

  redirectToSignIn = () => {
    const redirectTo = `${window.location.pathname}${window.location.search || ''}`;
    window.location.href = `/sign-in?redirectto=${encodeURIComponent(redirectTo)}`;
  };

  sendStartLog = async (payload) => {
    try {
      const url = '/api/v1/drops/link/start-log';
      const body = JSON.stringify(payload || {});

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      }

      await Axios.post(url, payload, { timeout: 1500 });
    } catch (e) {
      // ignore
    }
  };

  buildRedirectUri = (cfg) => {
    try {
      const linkRedirectUris = Array.isArray(cfg.linkRedirectUris) ? cfg.linkRedirectUris : [];
      const currentOrigin = window.location.origin;

      const findMatchingRedirectUri = () => {
        for (let i = 0; i < linkRedirectUris.length; i++) {
          const candidate = String(linkRedirectUris[i] || '').trim();
          if (!candidate) continue;
          try {
            if (new URL(candidate).origin === currentOrigin) return candidate;
          } catch (e) {
            // ignore invalid URL
          }
        }
        return null;
      };

      // If multiple redirect URIs are configured, pick the one matching the current origin (important for tunnel domains).
      // Otherwise fallback to the single configured redirect URI.
      const redirectUri = findMatchingRedirectUri() || cfg.linkRedirectUri || null;

      // Guard against a common misconfig: opening the link on the tunnel domain, but the redirect_uri points to localhost.
      try {
        const redirectOrigin = new URL(redirectUri).origin;
        if (redirectOrigin !== currentOrigin) {
          return { redirectUri: null, error: `Redirect URI origin mismatch (current=${currentOrigin}, configured=${redirectUri})` };
        }
      } catch (e) {
        // ignore
      }

      return { redirectUri, error: null };
    } catch (e) {
      return { redirectUri: null, error: e.message || 'Failed to compute redirectUri' };
    }
  };

  load = async () => {
    try {
      this.setState({ status: 'loading', error: null });
      const token = this.getToken();

      const routerQuery = this.props?.router?.location?.query || {};
      const parsedQuery = (() => {
        try {
          const u = new URL(window.location.href);
          const obj = {};
          for (const [k, v] of u.searchParams.entries()) obj[k] = v;
          return obj;
        } catch (e) {
          return {};
        }
      })();
      const query = { ...parsedQuery, ...routerQuery };
      const rawSearch = window.location.search || '';
      const rawHash = window.location.hash || '';
      const debug = String(query.debug || '') === '1';

      const resp = await Axios.get('/api/v1/drops/public-config');
      const cfg = resp?.data?.data || {};

      // Treat "drops app configured" as "client id exists" (OAuth start can happen).
      // `dropsAppEnabled` means "client id + secret are present" (link complete can happen server-side).
      const dropsAppConfigured = !!cfg.dropsAppClientId;
      const dropsUsesDedicatedCredentials = !!cfg.dropsAppUsesDedicatedCredentials;
      const dropsCampaignClientId = cfg?.dropsCampaignClientId ? String(cfg.dropsCampaignClientId).trim() : '';
      const dropsCampaignClientIdMismatch = !!cfg?.dropsCampaignClientIdMismatch;
      let redirectUri = null;
      let redirectError = null;
      // We only use the dedicated `/drops/link` flow when the Drops app has BOTH client_id + secret configured.
      if (dropsUsesDedicatedCredentials && dropsAppConfigured) {
        const computed = this.buildRedirectUri(cfg);
        redirectUri = computed.redirectUri;
        redirectError = computed.error;
        if (!redirectUri) {
          this.setState({ status: 'error', error: redirectError || 'Drops linking redirect URI is not configured.' });
          return;
        }
        try {
          sessionStorage.setItem('noizDropsLinkRedirectUri', redirectUri);
        } catch (e) {
          // ignore
        }
      }

      // Always log the start context.
      this.sendStartLog({
        href: window.location.href,
        origin: window.location.origin,
        path: window.location.pathname,
        query,
        rawSearch,
        rawHash,
        documentReferrer: (typeof document !== 'undefined' && document.referrer) ? document.referrer : '',
        oauthClientId: cfg.dropsAppClientId || null,
        redirectUri,
        state: null,
        hasNoizToken: !!token,
      });

      // Only block when the campaign client_id doesn't match the OAuth client_id we will use.
      // This ensures we can always fall back to the default Twitch app unless it would definitely fail the campaign linking check.
      if (dropsCampaignClientIdMismatch) {
        // eslint-disable-next-line no-console
        console.error('[DropsLinkStart] campaign client_id mismatch', {
          campaignClientId: dropsCampaignClientId || null,
          oauthClientId: cfg?.dropsAppClientId || null,
          dropsAppClientIdRaw: cfg?.dropsAppClientIdRaw || null,
          dropsAppUsesDedicatedCredentials: !!cfg?.dropsAppUsesDedicatedCredentials,
          dropsAppConfigMismatch: !!cfg?.dropsAppConfigMismatch,
        });
        this.setState({
          status: 'error',
          error: 'System configuration error. Please try again later.',
        });
        return;
      }

      let linkStatus = null;
      if (token) {
        try {
          const statusResp = await Axios.get('/api/v1/user/drops/link/status?validate=1', { headers: { Authorization: `JWT ${token}` } });
          linkStatus = statusResp?.data?.data || null;
        } catch (e) {
          const code = e?.response?.data?.code || e?.response?.data?.error;
          if ((e?.response?.status === 400) && (code === 'E_FORCED_LOGOUT')) {
            this.clearLocalAuth();
            this.redirectToSignIn();
            return;
          }
        }
      }

      this.setState({
        status: 'ready',
        cfg,
        linkStatus,
        computedRedirectUri: redirectUri,
        oauthUrl: null,
        debug,
      });

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('[DropsLinkStart] loaded', { cfg, linkStatus, redirectUri });
      }
    } catch (e) {
      this.setState({ status: 'error', error: e?.response?.data?.message || e.message || 'Failed to load linking config' });
    }
  };

  goSignIn = () => {
    const redirectTo = `${window.location.pathname}${window.location.search || ''}`;
    window.location.href = `/sign-in?redirectto=${encodeURIComponent(redirectTo)}`;
  };

  connectSteam = () => {
    const returnTo = `${window.location.origin}/drops/link/start`;
    const callbackURL = `${window.location.origin}/connect-accounts/steam?addr=${encodeURIComponent(returnTo)}&src=drops`;
    const token = this.getToken();

    const fallback = () => {
      window.location.href = `/connect-accounts/steam?addr=${encodeURIComponent(returnTo)}&src=drops`;
    };

    if (!token) return fallback();

    // Prefer token-driven start to avoid session/cookie issues (tunnel/proxy).
    fetch(`/api/v1/auth/steam/start?callbackURL=${encodeURIComponent(callbackURL)}`, {
      method: 'GET',
      headers: { Authorization: `JWT ${token}` },
      credentials: 'same-origin',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!body?.url) throw new Error('Missing url');
        window.location.href = body.url;
      })
      .catch(() => fallback());
  };

  startDropsTwitchOAuth = () => {
    const { cfg, computedRedirectUri } = this.state;
    const token = this.getToken();
    if (!token) return;

    const dropsUsesDedicatedCredentials = !!cfg?.dropsAppUsesDedicatedCredentials;
    const twitchClientId = cfg?.dropsAppClientId ? String(cfg.dropsAppClientId).trim() : '';
    if (!dropsUsesDedicatedCredentials || !twitchClientId) return;

    const redirectUri = computedRedirectUri || `${window.location.origin}/drops/link`;
    const scopes = Array.isArray(cfg?.scopes) && cfg.scopes.length ? cfg.scopes : ['user:read:email'];
    const state = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      sessionStorage.setItem(DROPS_STATE_KEY, state);
      sessionStorage.setItem('noizDropsLinkRedirectUri', redirectUri);
    } catch (e) {
      // ignore
    }

    const url =
      `https://id.twitch.tv/oauth2/authorize` +
      `?client_id=${encodeURIComponent(twitchClientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scopes.join(' '))}` +
      `&state=${encodeURIComponent(state)}`;

    // eslint-disable-next-line no-console
    console.log('[DropsLinkStart] dropsOAuthUrl=', url);
    window.location.href = url;
  };

  connectTwitch = () => {
    const { cfg } = this.state;
    const token = this.getToken();
    if (!token) return;

    // Only use the dedicated Drops linking flow (/drops/link) when Drops client_id+secret are configured.
    if (cfg?.dropsAppUsesDedicatedCredentials && cfg?.dropsAppClientId) {
      this.startDropsTwitchOAuth();
      return;
    }

    // Fallback: reuse existing Connect Accounts flow (default/legacy Twitch app).
    // Prefer token-driven auth start to avoid relying on session cookies.
    const returnTo = `${window.location.origin}/drops/link/start`;
    const callbackURL = `${window.location.origin}/connect-accounts/twitch?addr=${encodeURIComponent(returnTo)}&src=drops`;

    const fallback = () => {
      window.location.href = `/connect-accounts/twitch?addr=${encodeURIComponent(returnTo)}&src=drops`;
    };

    fetch(`/api/v1/auth/twitch/start?callbackURL=${encodeURIComponent(callbackURL)}`, {
      method: 'GET',
      headers: { Authorization: `JWT ${token}` },
      credentials: 'same-origin',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!body?.url) throw new Error('Missing url');
        window.location.href = body.url;
      })
      .catch(() => fallback());
  };

  render() {
    const { status, error, cfg, linkStatus, debug } = this.state;
    const token = this.getToken();
    const requiresSteam = !!(linkStatus?.requiresSteam || cfg?.requiresSteam);
    const steamConnected = !!linkStatus?.user?.steamConnected;
    const dropsLinked = !!linkStatus?.user?.dropsLinked;
    const needsSignIn = !token;
    const steamStepRequired = requiresSteam;
    const dropsUsesDedicatedCredentials = !!cfg?.dropsAppUsesDedicatedCredentials;
    const twitchLabel = dropsUsesDedicatedCredentials ? 'Drops app' : 'Default app';

    const noizUsername = this.props?.auth?.user?.username || this.props?.auth?.user?.email || null;
    const twitchUsername =
      linkStatus?.user?.connectedAccounts?.twitchDrops?.username ||
      linkStatus?.user?.connectedAccounts?.twitch?.username ||
      null;
    const steamUsername = linkStatus?.user?.connectedAccounts?.steam?.username || null;
    const dropsTokenValid = linkStatus?.user?.connectedAccounts?.twitchDrops?.tokenValid;
    const dropsNeedsReconnect = !!linkStatus?.user?.dropsNeedsReconnect;

    const twitchStepComplete = dropsUsesDedicatedCredentials
      ? !!dropsLinked
      : !!linkStatus?.user?.twitchConnected;
    const twitchReady = twitchStepComplete && !dropsNeedsReconnect && dropsTokenValid !== false;

    const stepNoiz = {
      key: 'noiz',
      title: 'SIGN IN TO YOUR ACCOUNT ON NOIZ.GG',
      account: noizUsername,
      icon: null,
      actionLabel: needsSignIn ? 'SIGN IN' : 'SIGNED IN',
      actionDisabled: !needsSignIn,
      onAction: this.goSignIn,
      hint: null,
    };

    const stepTwitch = {
      key: 'twitch',
      title: 'CONNECT YOUR TWITCH ACCOUNT',
      account: twitchUsername,
      icon: twitchLogo,
      actionLabel: twitchStepComplete ? 'CONNECTED' : (dropsNeedsReconnect || dropsTokenValid === false ? 'RECONNECT' : 'SIGN IN'),
      actionDisabled: !token || needsSignIn ? true : (twitchStepComplete && !dropsNeedsReconnect && dropsTokenValid !== false),
      onAction: this.connectTwitch,
      hint: dropsUsesDedicatedCredentials
        ? (
          ((dropsNeedsReconnect || dropsTokenValid === false) ? 'Twitch authorization is disconnected/expired. Please reconnect.' : `Uses ${twitchLabel}.`)
        )
        : 'Uses Default app.',
    };

    const stepSteam = {
      key: 'steam',
      title: 'CONNECT YOUR GAME PLATFORM',
      account: steamUsername,
      icon: steamLogo,
      actionLabel: steamConnected ? 'SIGNED IN' : 'SIGN IN',
      actionDisabled: !token || !twitchReady || steamConnected,
      onAction: this.connectSteam,
      hint: 'Required for this game.',
    };

    // Follow the intended flow: Noiz -> Twitch -> (optional) game platform
    const steps = steamStepRequired ? [stepNoiz, stepTwitch, stepSteam] : [stepNoiz, stepTwitch];

    return (
      <section className="DropsLinkStart">
        <section className="DropsLinkStart__panel">
          <header className="DropsLinkStart__header">
            <h1 className="DropsLinkStart__heroTitle">HOW TO RECEIVE REWARDS</h1>
            <div className="DropsLinkStart__heroUnderline" />
          </header>

          {status === 'loading' ? <div className="DropsPage__card"><div className="DropsPage__loading">Loading…</div></div> : null}
          {status === 'error' ? <div className="DropsPage__card"><div className="DropsPage__error">{error}</div></div> : null}

          {status === 'ready' ? (
            <>
              <section className="DropsLinkStart__steps">
                {steps.map((step, idx) => (
                  <div key={step.key} className="DropsLinkStart__stepCard">
                    <div className="DropsLinkStart__stepTop">
                      <div className="DropsLinkStart__stepLabel">STEP {String(idx + 1).padStart(2, '0')}</div>
                      <div className="DropsLinkStart__stepTitle">{step.title}</div>
                    </div>

                    {step.account ? (
                      <div className="DropsLinkStart__account">
                        ACCOUNT: <span className="DropsLinkStart__accountValue">{step.account}</span>
                      </div>
                    ) : (
                      <div className="DropsLinkStart__account DropsLinkStart__accountMuted">ACCOUNT: —</div>
                    )}

                    {step.hint ? <div className="DropsLinkStart__hint">{step.hint}</div> : null}

                    <div className="DropsLinkStart__ctaRow">
                      <button
                        type="button"
                        className="site-button DropsLinkStart__actionBtn"
                        onClick={step.onAction}
                        disabled={step.actionDisabled}
                      >
                        {step.icon ? <img className="DropsLinkStart__btnIcon" src={step.icon} alt="" /> : null}
                        {step.actionLabel}
                      </button>
                    </div>
                  </div>
                ))}
              </section>

              <div className="DropsLinkStart__footer">
                <a className="site-button" href="https://www.twitch.tv/drops/inventory">
                  Back to Twitch Inventory
                </a>
                <button type="button" className="site-button" onClick={this.load}>
                  Refresh Status
                </button>
                <a className="site-button" href="/drops">
                  Back to Drops
                </a>
              </div>

              {dropsLinked ? (
                <div className="DropsLinkStart__success">
                  Linked successfully. Go back to Twitch inventory and refresh.
                </div>
              ) : null}

              {debug ? (
                <pre className="DropsPage__debugOutput">{JSON.stringify({ cfg, linkStatus }, null, 2)}</pre>
              ) : null}
            </>
          ) : null}
        </section>
      </section>
    );
  }
}

export default DropsLinkStartPage;
